import { Img } from '@/components/builder2/utils/Img';
import React from 'react';
import { useNode } from '@craftjs/core';
import { Facebook, Instagram, Twitter, Youtube, Linkedin, MessageCircle, Globe } from 'lucide-react';
import { TikTokIcon } from '../../icons/TikTokIcon';
import useClientStore from '@/store/useClientStore';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { rubroDelPais } from '@/lib/country';
import { normalizeBuilderLink } from '@/utils/functions';
import { legacyFooterTarget } from '@/utils/footerLinks';
import { spansFooterModerno, type ColumnasPorFila } from '@/utils/footerLayout';
import { htmlSeguro } from '../../utils/htmlSeguro';

interface FooterLink { text: string; url: string; }
interface FooterColumn { title: string; links: FooterLink[]; }
interface SocialLink { platform: 'facebook' | 'instagram' | 'twitter' | 'youtube' | 'linkedin' | 'tiktok' | 'whatsapp'; url: string; }

// Empty stays empty (icon hides). WhatsApp accepts a bare phone → https://wa.me/<digits>.
const normalizeSocialUrl = (platform: string, raw: string): string => {
  const v = (raw || '').trim();
  if (!v || v === '#') return '';
  if (platform === 'whatsapp' && !/^https?:\/\//i.test(v)) {
    const digits = v.replace(/[^\d]/g, '');
    return digits ? `https://wa.me/${digits}` : '';
  }
  // En TikTok lo natural es copiar el usuario, no la URL.
  if (platform === 'tiktok' && v.startsWith('@')) return `https://www.tiktok.com/${v}`;
  // Sin esquema el href sale relativo y el link cae dentro del propio sitio.
  if (!/^https?:\/\//i.test(v)) return `https://${v}`;
  return v;
};

interface FooterModernoProps {
  companyName?: string; description?: string; columns?: FooterColumn[];
  socialLinks?: SocialLink[]; copyrightText?: string; bgColor?: string; textColor?: string;
  headingColor?: string; accentColor?: string; dividerColor?: string; socialIconBgColor?: string;
  logoUrl?: string;
  /** Alto del logo en px. Default 40 = el `h-10` que tuvo siempre (ticket #57). */
  logoHeight?: number;
  /** Columnas de enlaces por fila. 'auto' se ajusta a las que haya. */
  columnsPerRow?: ColumnasPorFila;
}

const SocialIcon = ({ platform, size = 18 }: { platform: string; size?: number }) => {
  switch (platform) {
    case 'facebook': return <Facebook size={size} />;
    case 'instagram': return <Instagram size={size} />;
    case 'twitter': return <Twitter size={size} />;
    case 'youtube': return <Youtube size={size} />;
    case 'linkedin': return <Linkedin size={size} />;
    case 'tiktok': return <TikTokIcon size={size} />;
    case 'whatsapp': return <MessageCircle size={size} />;
    // Una plataforma desconocida mostraba el logo de Facebook. Mejor un globo.
    default: return <Globe size={size} />;
  }
};

export const FooterModerno = ({
  companyName = 'GoAutos', description = 'Tu aliado de confianza en la compra de vehículos.',
  columns = [
    { title: 'Empresa', links: [{ text: 'Sobre nosotros', url: '/about' }, { text: 'Contacto', url: '/contact' }] },
    { title: 'Servicios', links: [{ text: 'Financiamiento', url: '/financing' }, { text: 'Consignaciones', url: '/consignments' }] },
    { title: 'Vehículos', links: [{ text: 'Ver catálogo', url: '/vehicles' }] },
  ],
  socialLinks = [{ platform: 'facebook', url: '' }, { platform: 'instagram', url: '' }, { platform: 'whatsapp', url: '' }],
  copyrightText = '', bgColor = '#0f172a', textColor = '#94a3b8',
  headingColor = '#ffffff', accentColor = '#3b82f6',
  dividerColor = 'rgba(255,255,255,0.08)', socialIconBgColor = 'rgba(255,255,255,0.06)',
  logoUrl = '',
  // Default en la firma: los árboles ya guardados no traen el prop.
  logoHeight = 40, columnsPerRow = 'auto',
}: FooterModernoProps) => {
  const { connectors } = useNode();
  const { client } = useClientStore();
  const { t } = useTranslation();

  // Pick logo based on bgColor luminance
  const logoDark = client?.logo_dark || '';
  const logoLight = client?.logo || '';
  const isDarkBg = (() => {
    const c = (bgColor || '').replace('#', '');
    if (c.length !== 6) return false;
    const r = parseInt(c.substring(0, 2), 16) / 255;
    const g = parseInt(c.substring(2, 4), 16) / 255;
    const b = parseInt(c.substring(4, 6), 16) / 255;
    return 0.299 * r + 0.587 * g + 0.114 * b < 0.4;
  })();
  const finalLogoUrl = logoUrl || (isDarkBg ? (logoDark || logoLight) : (logoLight || logoDark));
  const currentYear = new Date().getFullYear();
  const finalName = companyName || client?.name || rubroDelPais(client?.country);
  const finalCopyright = copyrightText || `© ${currentYear} ${finalName}. ${t('site.footer.rights')}`;

  // Los links de fábrica sin página real no se muestran (irían a un 404). Si a una
  // columna no le queda ninguno se descarta entera, para no dejar el título
  // flotando solo (le pasaba a "Legal"). El conteo define el ancho de las celdas.
  const columnasVisibles = (columns || [])
    .map((col) => ({
      col,
      visibles: (col.links || [])
        .map((link) => ({ link, legacy: legacyFooterTarget(link.url) }))
        .filter(({ legacy }) => !legacy?.hidden)
        .map(({ link, legacy }) => ({
          link,
          // `legacy.ruta` solo es null en los ocultos, ya filtrados arriba.
          href: legacy?.ruta ?? normalizeBuilderLink(link.url),
        })),
    }))
    .filter(({ visibles }) => visibles.length > 0);

  const spans = spansFooterModerno(columnasVisibles.length, columnsPerRow);

  return (
    <div ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }} style={{ backgroundColor: bgColor, color: textColor, position: 'relative' }} className="w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          <div className={spans.logo}>
            {finalLogoUrl ? (
              <Img variante="icono" src={finalLogoUrl} alt={finalName} className="w-auto mb-4 object-contain" style={{ height: `${logoHeight}px` }} />
            ) : (
              <h3 className="text-xl font-semibold mb-4 tracking-tight" style={{ color: headingColor }} dangerouslySetInnerHTML={htmlSeguro(finalName || '')} />
            )}
            <p className="text-[14px] leading-relaxed mb-6" style={{ color: textColor }} dangerouslySetInnerHTML={htmlSeguro(description || '')} />
            {(() => {
              const visible = socialLinks
                .map((s) => ({ ...s, url: normalizeSocialUrl(s.platform, s.url) }))
                .filter((s) => s.url);
              if (visible.length === 0) return null;
              return (
                <div className="flex items-center gap-2">
                  {visible.map((social, index) => (
                    <a key={index} href={social.url} target="_blank" rel="noopener noreferrer"
                      className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                      style={{ backgroundColor: socialIconBgColor, color: textColor }}>
                      <SocialIcon platform={social.platform} />
                    </a>
                  ))}
                </div>
              );
            })()}
          </div>
          {columnasVisibles.map(({ col, visibles }, i) => {
            return (
              <div key={i} className={`${spans.columna} lg:col-start-auto`}>
                <h4 className="text-[13px] font-semibold uppercase tracking-wider mb-5" style={{ color: headingColor }} dangerouslySetInnerHTML={htmlSeguro(col.title || '')} />
                <ul className="space-y-3">
                  {visibles.map(({ link, href }, j) => (
                    <li key={j}><a href={href} className="text-[14px] transition-colors duration-200" style={{ color: textColor }} dangerouslySetInnerHTML={htmlSeguro(link.text || '')} /></li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <div className="mt-14 pt-8 text-center" style={{ borderTop: `1px solid ${dividerColor}` }}>
          <p className="text-[13px]" style={{ color: `${textColor}99` }} dangerouslySetInnerHTML={htmlSeguro(finalCopyright || '')} />
        </div>
      </div>
    </div>
  );
};

FooterModerno.craft = {
  displayName: 'FooterModerno',
  props: {
    companyName: 'GoAutos', description: 'Tu aliado de confianza en la compra de vehículos.',
    columns: [
      { title: 'Empresa', links: [{ text: 'Sobre nosotros', url: '/about' }, { text: 'Contacto', url: '/contact' }] },
      { title: 'Servicios', links: [{ text: 'Financiamiento', url: '/financing' }, { text: 'Consignaciones', url: '/consignments' }] },
      { title: 'Vehículos', links: [{ text: 'Ver catálogo', url: '/vehicles' }] },
    ],
    socialLinks: [{ platform: 'facebook', url: '' }, { platform: 'instagram', url: '' }, { platform: 'whatsapp', url: '' }],
    copyrightText: '', bgColor: '#0f172a', textColor: '#94a3b8',
    headingColor: '#ffffff', accentColor: '#3b82f6',
    dividerColor: 'rgba(255,255,255,0.08)', socialIconBgColor: 'rgba(255,255,255,0.06)',
    logoUrl: '', columnsPerRow: 'auto',
  },
  rules: { canDrag: () => true, canDrop: () => true, canMoveIn: () => false },
};
